import React from 'react';
import { getAuth } from 'firebase/auth';
import {
    EstimateCost,
    EstimateGenerationPhase,
    GetWalletBalance,
    INSUFFICIENT_FUNDS_EVENT
} from '../Client/ManagementServiceClient';

/**
 * The prepaid balance, shared by everything that spends or displays it.
 *
 * One provider rather than a fetch per component, because the balance is shown
 * in the nav at all times AND changes as a side effect of work happening
 * elsewhere in the app. Components call `refresh()` after an AI action so the
 * number they just changed is the number the user sees.
 *
 * It also owns the out-of-funds prompt: a 402 can come back from any AI call,
 * and every one of them should raise the same top-up dialog rather than each
 * inventing its own error message.
 */
const WalletContext = React.createContext(null);

// Balances move when work completes in a background processor, which the client
// is never told about. Polling while the tab is visible keeps the displayed
// number roughly honest without a socket; hidden tabs stop, so a parked tab
// isn't making requests all day.
const POLL_INTERVAL_MS = 60_000;

const WalletProvider = ({ children }) => {
    const [balance, setBalance] = React.useState(null);
    const [loading, setLoading] = React.useState(true);
    const [error, setError] = React.useState('');
    // { shortfall_usd, message } while the out-of-funds dialog is open.
    const [fundingPrompt, setFundingPrompt] = React.useState(null);

    const refresh = React.useCallback(async () => {
        try {
            const auth = getAuth();
            if (!auth.currentUser) {
                setLoading(false);
                return null;
            }

            const idToken = await auth.currentUser.getIdToken();
            const { status, myJson } = await GetWalletBalance(idToken);

            if (status === 200) {
                setBalance(myJson);
                setError('');
                return myJson;
            }

            setError('Could not load your balance.');
            return null;
        } catch (e) {
            setError('Could not load your balance.');
            return null;
        } finally {
            setLoading(false);
        }
    }, []);

    React.useEffect(() => {
        refresh();

        const tick = () => {
            if (document.visibilityState === 'visible') {
                refresh();
            }
        };
        const timer = setInterval(tick, POLL_INTERVAL_MS);
        // Catch up immediately on return rather than waiting out the interval.
        document.addEventListener('visibilitychange', tick);

        return () => {
            clearInterval(timer);
            document.removeEventListener('visibilitychange', tick);
        };
    }, [refresh]);

    /**
     * Show the top-up dialog for a 402 body from any AI endpoint.
     * Returns true when it handled the response, so callers can write:
     *
     *   if (handleInsufficientFunds(status, myJson)) return;
     */
    const handleInsufficientFunds = React.useCallback((status, body) => {
        const detail = body?.detail || body;
        const isFundingError =
            status === 402 || detail?.error === 'insufficient_funds' || body?.code === 'insufficient_funds';

        if (!isFundingError) {
            return false;
        }

        setFundingPrompt({
            shortfallUsd: Number(detail?.shortfall_usd ?? body?.shortfall_usd ?? 0),
            availableUsd: Number(detail?.available_usd ?? 0),
            requiredUsd: Number(detail?.required_usd ?? 0),
            message:
                detail?.message ||
                body?.message ||
                'You do not have enough balance for this. Add funds to continue.'
        });
        // The balance the server just rejected against is authoritative.
        refresh();
        return true;
    }, [refresh]);

    const dismissFundingPrompt = React.useCallback(() => setFundingPrompt(null), []);

    // The estimate behind the open "this will cost $X — go ahead?" dialog, and
    // (in the ref) the caller's await: answering the dialog is what finishes
    // their `confirmCost(...)` call.
    const [costPrompt, setCostPrompt] = React.useState(null);
    const costAnswer = React.useRef(null);

    /**
     * Quote an operation, and get the user's answer if it is worth asking about.
     *
     * Usage at a call site is one line:
     *
     *   if (!await confirmCost({ flow: 'video_generation', generation: {...} })) return;
     *
     * Resolves true when the work should go ahead — either because the server
     * said the operation is too cheap to interrupt for, or because the user
     * said yes to the price. Resolves false only on an explicit "no".
     *
     * It FAILS OPEN: an estimate that cannot be fetched resolves true rather
     * than blocking the feature. A quote is an improvement on the existing
     * behaviour (spend, then see the balance move), not a new gate the work
     * depends on — and the reservation still refuses anything unaffordable.
     */
    const confirmCost = React.useCallback(async ({ flow, generation = null, params = {}, note = '' }) => {
        let estimate = null;
        try {
            const auth = getAuth();
            if (!auth.currentUser) return true;
            const idToken = await auth.currentUser.getIdToken();

            // A phase of an existing run is priced from the run itself; anything
            // else is priced from what the caller is about to send.
            const { status, myJson } = generation
                ? await EstimateGenerationPhase(idToken, generation.workspaceId, generation.params || {})
                : await EstimateCost(idToken, { entity_flow: flow, ...params });

            if (status !== 200) return true;
            estimate = myJson;
        } catch (e) {
            return true;
        }

        if (!estimate?.requires_confirmation) return true;

        return new Promise((resolve) => {
            // A second question while one is open supersedes it, and the one it
            // replaced answers "no" rather than leaving its caller awaiting a
            // dialog that is no longer on screen.
            if (costAnswer.current) costAnswer.current(false);
            costAnswer.current = resolve;
            setCostPrompt({ ...estimate, note });
        });
    }, []);

    /**
     * Quote an operation WITHOUT asking about it — for showing a price next to
     * a button before it is pressed.
     *
     * Deliberately the same endpoint `confirmCost` quotes from, rather than a
     * second pricing path: a figure shown on a page and the figure in the
     * confirmation dialog that follows it must be the same number, or the
     * dialog reads as a bait and switch.
     *
     * Returns null rather than throwing when the quote cannot be had. A price
     * label is an improvement on showing nothing; it is not something a
     * feature should break over.
     */
    const quoteCost = React.useCallback(async ({ flow, params = {} }) => {
        try {
            const auth = getAuth();
            if (!auth.currentUser) return null;
            const idToken = await auth.currentUser.getIdToken();
            const { status, myJson } = await EstimateCost(idToken, { entity_flow: flow, ...params });
            return status === 200 ? myJson : null;
        } catch (e) {
            return null;
        }
    }, []);

    // Closing the dialog any other way (backdrop, Cancel) is a "no": the safe
    // answer to "shall I spend your money?" is never yes by default.
    const answerCostPrompt = React.useCallback((answer) => {
        const resolve = costAnswer.current;
        costAnswer.current = null;
        setCostPrompt(null);
        if (resolve) resolve(Boolean(answer));
    }, []);

    // Every 402 from any endpoint arrives here, because the API client raises
    // this event centrally rather than each call site handling it.
    React.useEffect(() => {
        const onInsufficientFunds = (event) => handleInsufficientFunds(402, event.detail);
        window.addEventListener(INSUFFICIENT_FUNDS_EVENT, onInsufficientFunds);
        return () => window.removeEventListener(INSUFFICIENT_FUNDS_EVENT, onInsufficientFunds);
    }, [handleInsufficientFunds]);

    const value = React.useMemo(() => ({
        balance,
        availableUsd: balance?.available_usd ?? 0,
        localCreditEnabled: Boolean(balance?.local_credit_enabled),
        loading,
        error,
        refresh,
        fundingPrompt,
        handleInsufficientFunds,
        dismissFundingPrompt,
        confirmCost,
        quoteCost,
        costPrompt,
        answerCostPrompt
    }), [
        balance, loading, error, refresh, fundingPrompt, handleInsufficientFunds,
        dismissFundingPrompt, confirmCost, quoteCost, costPrompt, answerCostPrompt
    ]);

    return <WalletContext.Provider value={value}>{children}</WalletContext.Provider>;
};

export { WalletContext, WalletProvider };
